import { Component } from "react"

/* Modal */
import Modal from "react-modal"

/* Components */
import { Icon } from "../../components"

/* Icons */
import { LoadingOutlined } from "@ant-design/icons"

/* REST API */
import { get } from "../../api/Promo"

/* Widgets */
import { toast } from "../common"

/* Shared */
import { stateOf, monthsText, usesText, untilText } from "./shared"
import { SUBSCRIPTION_STATUSES } from "../organization/shared"
import { ago, initialsOf, avatarColorOf } from "../user/shared"
import { plural } from "../category/shared"

/* Moment */
import moment from "moment"

Modal.setAppElement("#root")


/* Widget Promo Modal — one promo code and every point which opened with it: the owner, the free
   months it got and whether the point went on paying after them — the whole point of a code.
   It is opened by the reference: this._promo.current.open(item).
   Props: onOrganization(id), onEdit(promo) — both optional */
class PromoModal extends Component {

    constructor() {
        super()

        this.state = {
            visible: false,
            status: "loading",
            data: null,
            item: null
        }
    }

    open = item => {
        this.setState({ visible: true, item, status: "loading", data: null }, () => this.load())
    }

    close = () => {
        this.setState({ visible: false })
    }

    load = () => {

        const { item } = this.state

        get({ id: item.id }).then(response => {
            if (response && response.status === 200) {
                this.setState({ status: "ok", data: response.data })
            }
            else {
                this.setState({ status: "error" })
            }
        }).catch(() => this.setState({ status: "error" }))
    }

    copy = text => {

        if (!navigator.clipboard || !text) {
            return
        }

        navigator.clipboard.writeText(text).then(() => toast.success("Скопировано")).catch(() => { })
    }

    /* Another card opens — this one closes first */
    go = (handler, value) => {

        if (!handler) {
            return
        }

        this.setState({ visible: false })
        handler(value)
    }

    /* The top — the code, its state and its terms */
    _hero = () => {

        /* Fields */
        const { item, data } = this.state
        const { onEdit } = this.props

        /* The card of the code does not carry who created it and when it was used last — the row of the list does */
        let promo = item

        if (data) {
            promo = { ...item, ...data.promo }
        }

        const state = stateOf(promo.state)

        return (
            <div className="promo-hero">

                <div className="promo-hero-top">
                    <div onClick={() => this.copy(promo.code)} className="promo-code large" title="Скопировать">
                        <Icon name="ticket" size={18} width={2} />
                        {promo.code}
                    </div>
                    <div className={`user-badge ${state.accent}`} title={state.hint}><i />{state.text}</div>
                </div>

                {promo.description && <div className="promo-hero-description">{promo.description}</div>}

                <div className="transaction-facts">
                    <div><span>Бесплатно</span><b>{monthsText(promo.months)}</b></div>
                    <div><span>Использован</span><b>{usesText(promo)}</b></div>
                    <div><span>Срок</span><b>{untilText(promo.validUntil)}</b></div>
                    <div><span>Последний раз</span><b>{promo.lastUsedAt ? ago(promo.lastUsedAt) : "ещё не вводили"}</b></div>
                    <div><span>Создан</span><b>{moment(promo.createdAt).format("D MMMM YYYY")}</b></div>
                    <div><span>Кто создал</span><b>{promo.adminName ? promo.adminName : "—"}</b></div>
                </div>

                {onEdit &&
                    <div onClick={() => this.go(onEdit, promo)} className="promo-hero-edit">
                        <Icon name="edit" size={15} width={2} />
                        Изменить промокод
                    </div>
                }

            </div>
        )
    }

    /* One point which used the code */
    _use = use => {

        /* Fields */
        const { onOrganization } = this.props

        const subscription = SUBSCRIPTION_STATUSES[use.subscriptionStatus]
        const name = use.memberName && `${use.memberName}`.trim().length > 0 ? `${use.memberName}`.trim() : "Без имени"
        const link = onOrganization && use.organizationID

        return (
            <div key={`${use.id}`} onClick={() => link && this.go(onOrganization, use.organizationID)} className={`master-team ${link ? "link" : ""}`}>

                <div className="promo-use-avatar" style={{ background: avatarColorOf(use.organizationID ? use.organizationID : use.id) }}>
                    {initialsOf(use.organizationName ? use.organizationName : name)}
                </div>

                <div className="master-team-text">
                    <div className="master-team-name">
                        <b>{use.organizationName ? use.organizationName : "Точка удалена"}</b>
                        {use.paid && <div className="user-badge green"><Icon name="check" size={11} width={2.6} />Платит</div>}
                    </div>
                    <div className="master-team-meta">
                        <span>{name}{use.memberPhone ? ` · ${use.memberPhone}` : ""}</span>
                        <span>ввёл {moment(use.createdAt).format("D MMM YYYY")}</span>
                        {use.code && <span>код {use.code}</span>}
                    </div>
                </div>

                <div className="master-team-numbers">
                    <b>{monthsText(use.months)}</b>
                    <span>бесплатно до {moment(use.freeUntil).format("D MMM YYYY")}</span>
                    {subscription && <span className={`promo-use-status ${subscription.color}`}>{subscription.text}{use.subscribedUntil ? ` · до ${moment(use.subscribedUntil).format("D MMM")}` : ""}</span>}
                </div>

                {link && <div className="master-team-arrow"><Icon name="right" size={14} width={2} /></div>}

            </div>
        )
    }

    _body = () => {

        /* Fields */
        const { status, data } = this.state

        if (status === "loading") {
            return (
                <>
                    {this._hero()}
                    <div className="wide-modal-loading"><LoadingOutlined /></div>
                </>
            )
        }

        if (status !== "ok") {
            return (
                <div className="wide-modal-loading">
                    <Icon name="alert" size={38} width={1.5} />
                    <p>Не удалось загрузить промокод</p>
                </div>
            )
        }

        const { uses, paid } = data
        const months = uses.reduce((sum, use) => sum + (parseInt(use.months) || 0), 0)

        return (
            <>
                {this._hero()}

                <div className="user-section">

                    <div className="user-section-title">
                        Точки по промокоду
                        {uses.length > 0 && <span>{uses.length} {plural(uses.length, "точка", "точки", "точек")} · {monthsText(months)} бесплатно · платят {paid} из {uses.length}</span>}
                    </div>

                    {uses.length === 0 && <div className="promo-empty">Этот код ещё никто не вводил</div>}

                    <div className="master-teams">
                        {uses.map(use => this._use(use))}
                    </div>

                </div>
            </>
        )
    }

    render = () => {

        /* Fields */
        const { visible, item } = this.state

        return (
            <Modal
                isOpen={visible}
                onRequestClose={this.close}
                className="wide-modal large user-modal"
                overlayClassName="wide-modal-overlay"
                closeTimeoutMS={200}
            >

                <div className="wide-modal-header">
                    <h2>Промокод</h2>
                    <div onClick={this.close} className="wide-modal-close">
                        <Icon name="close" size={11} width={2.2} />
                    </div>
                </div>

                {visible && item &&
                    <div className="wide-modal-body">
                        {this._body()}
                    </div>
                }

            </Modal>
        )
    }
}

export default PromoModal
